import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { type NavItem } from '@/types';
import { Link, useLocation } from 'react-router';

function getCleanPath(url: string | undefined | null): string {
    if (!url || typeof url !== 'string') return '';
    return url.split('?')[0].split('#')[0];
}

function getCleanUrl(url: string | undefined | null): string {
    if (!url || typeof url !== 'string') return '';
    return url.split('#')[0];
}

function findBestMatchIndex(currentUrl: string, items: NavItem[]): number {
    const currentPath = getCleanPath(currentUrl);
    const currentFullUrl = getCleanUrl(currentUrl);

    // For single-item groups (e.g., Overview, Settings), require an exact match
    // so they don't stay active when navigating to more specific routes.
    if (items.length === 1) {
        const [item] = items;
        const itemHref = getCleanUrl(item.href);
        const itemPath = getCleanPath(item.href);
        const hasQuery = itemHref.includes('?');

        const matches = hasQuery
            ? currentFullUrl === itemHref
            : currentPath === itemPath;

        return matches ? 0 : -1;
    }

    let bestMatchIndex = -1;
    let bestMatchScore = -1;

    items.forEach((item, index) => {
        const itemHref = getCleanUrl(item.href);
        const itemPath = getCleanPath(item.href);
        const hasQuery = itemHref.includes('?');
        const pathMatches = hasQuery
            ? currentFullUrl === itemHref
            : currentPath === itemPath || currentPath.startsWith(itemPath + '/');

        if (pathMatches && itemHref.length > bestMatchScore) {
            bestMatchScore = itemHref.length;
            bestMatchIndex = index;
        }
    });

    return bestMatchIndex;
}

export function NavMain({
    items = [],
    label,
    className,
}: {
    items: NavItem[];
    label?: string;
    className?: string;
}) {
    const { pathname, search } = useLocation();
    const activeIndex = findBestMatchIndex(`${pathname}${search}`, items);

    return (
        <SidebarGroup className={cn('px-2 py-0', className)}>
            {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
            <SidebarMenu className="gap-0.5">
                {items.map((item, index) => (
                    <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                            asChild
                            size="sm"
                            isActive={index === activeIndex}
                            tooltip={{ children: item.title }}
                        >
                            <Link to={item.href}>
                                {item.icon && <item.icon />}
                                <span>{item.title}</span>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    );
}

export default NavMain;
